import * as React from 'react';
import { HTMLAttributes } from 'react';
import { TypographyBaseProps } from './TypographyTypes';
import { useTypographyTheme } from '../../../themes';

/**
 * The HTML Element wrapper type for the Typography component.
 * @param {HTMLAttributes<any>} htmlProps - The HTML props to be passed to the Typography component.
 * @param {TypographyBaseProps} props - The props to be passed to the Typography component.
 */
type HTMLComponentProps = {
  htmlProps: HTMLAttributes<any>;
  typographyProps: TypographyBaseProps;
};

const H1 = (props: HTMLComponentProps) => (
  <h1 {...props.htmlProps} {...props.typographyProps} />
);
const H2 = (props: HTMLComponentProps) => (
  <h2 {...props.htmlProps} {...props.typographyProps} />
);
const H3 = (props: HTMLComponentProps) => (
  <h3 {...props.htmlProps} {...props.typographyProps} />
);
const H4 = (props: HTMLComponentProps) => (
  <h4 {...props.htmlProps} {...props.typographyProps} />
);
const H5 = (props: HTMLComponentProps) => (
  <h5 {...props.htmlProps} {...props.typographyProps} />
);
const H6 = (props: HTMLComponentProps) => (
  <h6 {...props.htmlProps} {...props.typographyProps} />
);
const P = (props: HTMLComponentProps) => (
  <p {...props.htmlProps} {...props.typographyProps} />
);
const Div = (props: HTMLComponentProps) => (
  <div {...props.htmlProps} {...props.typographyProps} />
);
const Span = (props: HTMLComponentProps) => (
  <span {...props.htmlProps} {...props.typographyProps} />
);

const TagComponents = {
  h1: H1,
  h2: H2,
  h3: H3,
  h4: H4,
  h5: H5,
  h6: H6,
  p: P,
  div: Div,
  span: Span,
};

/**
 * All the Copy and Text elements use this component as a base component.
 */
export const Typography = ({
  bold = false,
  children,
  color,
  italic = false,
  responsive = false,
  selectable = true,
  tag = 'span',
  type = 'body-01',
  textAlign,
}: TypographyBaseProps) => {
  const { typographyTheme } = useTypographyTheme();
  const classes = [
    typographyTheme['bsw-typography'],
    typographyTheme[responsive ? 'auto' : ''],
    typographyTheme[type],
    bold ? typographyTheme['txt-bold'] : undefined,
    italic ? typographyTheme['txt-italic'] : undefined
  ].join(' ');

  const _color = color && typographyTheme[`${color.replace('$', '')}`];

  const props: HTMLComponentProps = {
    htmlProps: {
      className: classes,
      style: {
        userSelect: selectable ? 'auto' : 'none',
        color: _color,
        textAlign,
      },
    },
    typographyProps: {
      children,
    },
  };

  const HTMLComponent = TagComponents[tag];

  return <HTMLComponent {...props} />;
};
